<template lang="pug">
.svg-container(:style='{ width: width, height: height }')
  SimpleSVG(
    :custom-id='svgId',
    @load='onSVGLoaded',
    @mouseleave.native='mouseLeave',
    @mouseover.native='mouseOver',
    v-bind='svgProps'
  )
</template>

<script setup lang="ts">
  import { computed, ref, watch, onMounted } from 'vue'
  //@ts-ignore
  import { SimpleSVG } from 'vue-simple-svg'

  const props = defineProps({
    icon: {
      required: true,
      type: String,
    },
    strokeClass: {
      type: String,
      default: 'icon-stroke',
    },
    secondaryStrokeClass: {
      type: String,
      default: 'icon-stroke-secondary',
    },
    strokeColor: {
      default: '#fff',
    },
    secondaryStrokeColor: {
      default: '#fff',
    },
    strokeHoverColor: {
      default: '#53F1B8',
    },
    fillClass: {
      type: String,
      default: 'icon-fill',
    },
    fillColor: {
      default: '#fff',
    },
    secondaryFillClass: {
      type: String,
      default: 'icon-fill-secondary',
    },
    secondaryFillColor: {
      default: '#fff',
    },
    hasHover: {
      type: Boolean,
      default: true,
    },
    fillHoverColor: {
      default: '#53F1B8',
    },
    toggleHover: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: 'auto',
    },
    height: {
      type: String,
      default: 'auto',
    },
  })

  const svgId = `svg-id-${Math.floor(Math.random() * 100000000000)}`

  const isHovering = ref(false)
  const svgElement = ref<HTMLElement | null>(null)
  const isLoaded = ref(false)

  const currentFillColor = computed(() =>
    props.toggleHover || isHovering.value ? props.fillHoverColor : props.fillColor,
  )

  const currentStrokeColor = computed(() =>
    props.toggleHover || isHovering.value ? props.strokeHoverColor : props.strokeColor,
  )

  const svgProps = computed(() => ({
    src: props.icon,
    width: '100%',
    height: '100%',
    fill: currentFillColor.value,
    fillClassName: props.fillClass,
    stroke: currentStrokeColor.value,
    strokeClassName: props.strokeClass,
  }))

  const mouseLeave = () => {
    if (props.hasHover) {
      isHovering.value = false
    }
  }

  const mouseOver = () => {
    if (props.hasHover) {
      isHovering.value = true
    }
  }

  const updateSecondaryColors = () => {
    if (!svgElement.value || !isLoaded.value) return

    requestAnimationFrame(() => {
      const strokeEls = svgElement.value?.querySelectorAll(`.${props.secondaryStrokeClass}`)
      strokeEls?.forEach((path: Element) => {
        ;(path as SVGPathElement).style.setProperty('stroke', props.secondaryStrokeColor)
      })

      const fillEls = svgElement.value?.querySelectorAll(`.${props.secondaryFillClass}`)
      fillEls?.forEach((path: Element) => {
        ;(path as SVGPathElement).style.setProperty('fill', props.secondaryFillColor)
      })
    })
  }

  const onSVGLoaded = () => {
    isLoaded.value = true
    svgElement.value = document.getElementById(svgId)
    updateSecondaryColors()
  }

  watch(
    () => [props.secondaryStrokeColor, props.secondaryFillColor],
    () => {
      updateSecondaryColors()
    },
    { deep: true },
  )

  watch(
    () => isHovering.value,
    () => {
      updateSecondaryColors()
    },
  )

  onMounted(() => {
    if (props.width === 'auto' || props.height === 'auto') {
      const container = document.querySelector(`#${svgId}`)?.parentElement
      if (container) {
        container.style.display = 'inline-block'
      }
    }
  })
</script>

<style lang="postcss" scoped>
  .svg-container {
    @apply ketch-inline-block ketch-leading-[0] ketch-align-middle;
  }
</style>
